/**
 * @file AddEditNewGroupForm.js
 * @description The form component for adding or editing a population group with optimized API calls.
 */

import React, {
    useState,
    useContext,
    useEffect,
    useCallback,
    useRef,
} from 'react';
import PropTypes from 'prop-types';
import {
    addGroup,
    updateGroup,
    fetchItems,
    fetchOperators,
    fetchPopulationGroupByID,
} from '../../services/populationGroupService';
import {
    fetchLocations,
    fetchDepartments,
    fetchDivisions,
    fetchRegions,
    fetchPayGroups,
    fetchBusinessUnits,
} from '../../services/orgLevelService';
import {
    fetchEmploymentStatuses,
    fetchTiers,
    fetchTenantUserStatuses,
    fetchEmploymentCategories,
    fetchLocales,
} from '../../services/systemService';
import { searchIndividuals } from '../../services/individualService';
import { searchBenefitPlans } from '../../services/benefitPlanService';
import styles from './PopulationGroup.module.scss';
import SideDrawer from '../helpers/SideDrawer';
import ViewGroupDrawer from './ViewGroupDrawer';
import { ModalContext, ModalTypes } from '../../store/ModalContext';
import Spinner from '../ui/spinners/Spinner';
import Toast from '../ui/toasts/Toast';
import { SearchableDropdown } from '../helpers/SearchableDropdown';
import { Dropdown } from '../helpers/Dropdown';
import { customMonthOptions, getUsStatesTerritories } from '../../utils/utils';
import { SecurityAttributes } from '../../utils/data/enums';
import { useAuthContext } from '../../store/Contexts';
import { getClientLocales } from '../../services/clientService';
import '../branding/BrandingPage.scss';
import { queryPlanTypes } from '../../services/clientService';
import { getText } from '../../i18n';

const AUTO_APPEND_SEGMENT = false;

/**
 * Sections for the SideDrawer, when the user clicks "View Term Definitions"
 * @example [ { label: 'Contains', text: <>A description...<b>Example:</b> ... </> }, ... ]
 */
const OPERATOR_DEFINITION_SECTIONS = [
    {
        label: 'Contains',
        text: (
            <>
                {getText('msg_3c627b0ca0a3')}
                <br />
                {getText('msg_c63737abd734')}{' '}
                {'"Email Address" Contains "gmail.com"'}
            </>
        ),
    },
    {
        label: "Doesn't Contain",
        text: (
            <>
                {getText('msg_994f9ca4c3fa')}
                <br />
                {getText('msg_c63737abd734')}{' '}
                {'"Job Title" doesn\'t contain "manager"'}
            </>
        ),
    },
    {
        label: 'Ends With',
        text: (
            <>
                {getText('msg_540261af8251')}
                <br />
                {getText('msg_c63737abd734')}{' '}
                {'"Phone number" Ends with "1234"'}
            </>
        ),
    },
    {
        label: 'In',
        text: (
            <>
                {getText('msg_d6172c07f33a')}
                <br />
                {getText('msg_c63737abd734')}{' '}
                {'"Country" in "USA, Canada, Mexico"'}
            </>
        ),
    },
    {
        label: 'Not In',
        text: (
            <>
                {getText('msg_4a0c104379b9')}
                <br />
                {getText('msg_c63737abd734')}{' '}
                {'"Status" not in "Inactive, Pending"'}
            </>
        ),
    },
    {
        label: 'Starts With',
        text: (
            <>
                {getText('msg_b79ed5d2614c')}
                <br />
                {getText('msg_c63737abd734')} {'Last_Name Starts with "Mc"'}
            </>
        ),
    },
    {
        label: 'Equal To',
        text: (
            <>
                {getText('msg_e0fc8e3562f4')}
                <br />
                {getText('msg_c63737abd734')} {'Age equals 65'}
            </>
        ),
    },
    {
        label: 'Not Equal To',
        text: (
            <>
                {getText('msg_1acd018e35d0')}
                <br />
                {getText('msg_c63737abd734')}{' '}
                {'"Pay group" is not equal to "part-time"'}
            </>
        ),
    },
    {
        label: 'Greater Than',
        text: (
            <>
                {getText('msg_4f1513f63cbc')}
                <br />
                {getText('msg_c63737abd734')}{' '}
                {'"Income" is greater than "50,000"'}
            </>
        ),
    },
    {
        label: 'Greater Than or Equal To',
        text: (
            <>
                {getText('msg_dde55a3ec2c8')}
                <br />
                {getText('msg_c63737abd734')}{' '}
                {'"Years of service" is greater than or equal to "10 years"'}
            </>
        ),
    },
    {
        label: 'Less Than or Equal To',
        text: (
            <>
                {getText('msg_9d65ce1fe1c4')}
                <br />
                {getText('msg_c63737abd734')}{' '}
                {'Hire_Date is less than or equal to 90 days'}
            </>
        ),
    },
];

/**
 * Renders the form component for adding or editing a population group.
 * @param {Object} props - The component props.
 * @param {Function} props.onAddEditGroup - The function to handle adding or editing a group.
 * @param {Function} props.onReturnToList - The function to handle returning to the group list.
 * @param {string} props.groupId - The ID of the group being edited, if applicable.
 * @returns {JSX.Element} The JSX element representing the form component.
 */
const AddEditNewGroupForm = ({ onAddEditGroup, onReturnToList, groupId }) => {
    const { showModal } = useContext(ModalContext);
    const { hasAttribute } = useAuthContext();

    const [dropdownData, setDropdownData] = useState({
        locations: [],
        departments: [],
        divisions: [],
        statuses: [],
        fulltimeOrParttime: [
            { id: 1, name: 'Full Time', code: 'FT' },
            { id: 2, name: 'Part Time', code: 'PT' },
        ],
        employeeIDs: [],
        tiers: [],
        planTypes: [],
        regions: [],
        payGroups: [],
        businessUnits: [],
        languages: [],
        accountStatuses: [],
        categories: [],
        workStates: [],
        plans: [],
        months: customMonthOptions(),
    });

    const loadedData = useRef({});

    const [initialGroupState, setInitialGroupState] = useState({
        id: null,
        name: '',
        segments: [{ item: null, operator: null, value: null }],
        errors: [{ item: null, operator: null, value: null }],
    });
    const [nameClassName, setNameClassName] = useState('');
    const [isLoading, setIsLoading] = useState(true);
    const [isDefinitionsDrawerOpen, setIsDefinitionsDrawerOpen] =
        useState(false);
    const [isViewGroupDrawerOpen, setIsViewGroupDrawerOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [operators, setOperators] = useState([]);
    const [group, setGroup] = useState({
        id: null,
        name: '',
        segments: [{ item: null, operator: null, value: null }],
        errors: [{ item: null, operator: null, value: null }],
    });

    const dataFetchMap = {
        1: { key: 'locations', fetch: fetchLocations },
        2: { key: 'departments', fetch: fetchDepartments },
        3: { key: 'divisions', fetch: fetchDivisions },
        5: { key: 'planTypes', fetch: queryPlanTypes },
        6: {
            key: 'plans',
            fetch: searchBenefitPlans,
            transform: data => data.viewModels || data,
        },
        7: { key: 'tiers', fetch: fetchTiers },
        10: { key: 'statuses', fetch: fetchEmploymentStatuses },
        12: {
            key: 'employeeIDs',
            fetch: searchIndividuals,
            transform: data =>
                data.individuals.map(individual => ({
                    ...individual,
                    code: individual.uniqueId,
                    name: `${individual.firstName} ${individual.lastName} (${individual.uniqueId})`,
                })),
        },
        39: { key: 'regions', fetch: fetchRegions },
        40: { key: 'businessUnits', fetch: fetchBusinessUnits },
        41: { key: 'payGroups', fetch: fetchPayGroups },
        42: { key: 'categories', fetch: fetchEmploymentCategories },
        43: { key: 'accountStatuses', fetch: fetchTenantUserStatuses },
        44: { key: 'languages', fetch: () => fetchLanguages() },
        55: { key: 'workStates', fetch: fetchWorkStates },
    };

    async function fetchWorkStates() {
        return getUsStatesTerritories().map(state => ({
            id: state.acronym,
            name: state.name,
            code: state.acronym,
        }));
    }

    const fetchLanguages = async () => {
        try {
            const [allSystemLanguages, tenantLocales] = await Promise.all([
                fetchLocales(),
                getClientLocales(),
            ]);

            const systemLanguages = (allSystemLanguages ?? []).map(lang => ({
                name: lang.name,
                id: lang.id,
                code: lang.code,
                isDefault: lang.isDefault,
            }));

            const selectedLocales = [];

            tenantLocales.map(tenantLocale => {
                var locale = systemLanguages.find(
                    l => l.id === tenantLocale.localeId,
                );

                selectedLocales.push(locale);
            });

            // if there arent any selected default to english
            if (!selectedLocales.length) {
                selectedLocales.push(systemLanguages.find(l => l.isDefault));
            }

            return selectedLocales;
        } catch (error) {
            console.error('Could not fetch languages:', error);
        }
    };

    // Load data for a specific item type
    const loadDataForItem = useCallback(async itemId => {
        if (!itemId || loadedData.current[itemId]) return;

        const dataConfig = dataFetchMap[itemId];
        if (!dataConfig) return;

        try {
            const data = await dataConfig.fetch();
            const transformedData = dataConfig.transform
                ? dataConfig.transform(data)
                : data;

            setDropdownData(prev => ({
                ...prev,
                [dataConfig.key]: transformedData,
            }));

            loadedData.current[itemId] = true;
        } catch (error) {
            console.error(`Could not fetch data for item ${itemId}:`, error);
            Toast.error(`Failed to load dropdown data for ${itemId}`);
        }
    }, []);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const [itemsData, operatorsData, selectedGroup = null] =
                    await Promise.all([
                        fetchItems(),
                        fetchOperators(),
                        groupId
                            ? fetchPopulationGroupByID(groupId)
                            : Promise.resolve(null),
                    ]);

                setItems(itemsData);
                setOperators(operatorsData);

                if (selectedGroup) {
                    const itemsToLoad = selectedGroup.segments
                        .map(segment => segment.itemId?.toString())
                        .filter(Boolean);

                    await Promise.all(itemsToLoad.map(loadDataForItem));
                    populateExistingGroup(selectedGroup);
                }
            } catch (error) {
                console.error('Fetching initial data failed:', error);
                Toast.error(getText('msg_99dce1db1d4a'));
            } finally {
                setIsLoading(false);
            }
        };

        fetchInitialData();
    }, [groupId, loadDataForItem]);

    const handleSegmentChange = async (index, key, value) => {
        const updatedSegments = [...group.segments];
        const updatedErrors = [...group.errors];

        if (!updatedSegments[index]) {
            updatedSegments[index] = { item: '', operator: '', value: '' };
            updatedErrors[index] = { item: null, operator: null, value: null };
        }

        updatedSegments[index][key] = value;
        updatedErrors[index][key] = !value ? true : null;

        if (key === 'item' && value) {
            await loadDataForItem(value);
        }

        const isLastSegment = index === updatedSegments.length - 1;
        const lastSegment = updatedSegments[updatedSegments.length - 1];
        const lastSegmentFieldsPopulated =
            lastSegment.item || lastSegment.operator || lastSegment.value;

        if (
            AUTO_APPEND_SEGMENT &&
            isLastSegment &&
            lastSegmentFieldsPopulated
        ) {
            updatedSegments.push({ item: '', operator: '', value: '' });
            updatedErrors.push({ item: null, operator: null, value: null });
        }

        setGroup({
            ...group,
            segments: updatedSegments,
            errors: updatedErrors,
        });
    };

    const populateExistingGroup = currentGroup => {
        if (currentGroup) {
            const currentState = {
                ...initialGroupState,
                id: currentGroup.id,
                name: currentGroup.name,
                segments: [
                    ...currentGroup.segments.map(segment => ({
                        ...segment,
                        item: segment.itemId ? segment.itemId.toString() : '',
                        operator: segment.operatorId
                            ? segment.operatorId.toString()
                            : '',
                    })),
                ],
            };

            const copiedObject = JSON.parse(JSON.stringify(currentState));
            setInitialGroupState(copiedObject);

            setGroup({
                ...currentState,
                segments: AUTO_APPEND_SEGMENT
                    ? [
                          ...currentState.segments,
                          { item: '', operator: '', value: '' },
                      ]
                    : [...currentState.segments],
                errors: AUTO_APPEND_SEGMENT
                    ? [
                          ...currentState.segments.map(() => ({
                              item: null,
                              operator: null,
                              value: null,
                          })),
                          { item: null, operator: null, value: null },
                      ]
                    : currentState.segments.map(() => ({
                          item: null,
                          operator: null,
                          value: null,
                      })),
            });
        } else {
            setGroup({
                id: null,
                name: '',
                segments: [{ item: '', operator: '', value: '' }],
                errors: [{ item: null, operator: null, value: null }],
            });
            setInitialGroupState({
                id: null,
                name: '',
                segments: [{ item: '', operator: '', value: '' }],
                errors: [{ item: null, operator: null, value: null }],
            });
        }
    };

    const handleRemoveSegment = index => {
        const updatedSegments = [...group.segments].filter(
            (_, i) => i !== index,
        );
        setGroup({ ...group, segments: updatedSegments });
    };

    const isFormDirty = () => {
        return !isGroupEdited(
            { ...initialGroupState, errors: [] },
            { ...group, errors: [] },
        );
    };

    const validateForm = () => {
        let isFormValid = true;

        if (!group.name || !group.name.trim()) {
            setNameClassName('is-invalid');
            Toast.error(getText('msg_0a188e9c2768'));
            isFormValid = false;
        } else {
            setNameClassName('');
        }

        // Validation for segments
        const dynamicSegmentValidation = group.segments.map(
            (segment, index) => {
                const isLastSegment = index === group.segments.length - 1;
                const isOnlyOneSegment = group.segments.length === 1;
                const isSegmentEmpty =
                    !segment.item &&
                    !segment.operator &&
                    !segment.value?.trim?.();

                const segmentErrors = {
                    item: null,
                    operator: null,
                    value: null,
                };

                if (
                    !isLastSegment ||
                    (isLastSegment && (!isSegmentEmpty || isOnlyOneSegment))
                ) {
                    if (!segment.item) {
                        segmentErrors.item = true;
                        isFormValid = false;
                    }
                    if (!segment.operator) {
                        segmentErrors.operator = true;
                        isFormValid = false;
                    }
                    if (segment.value?.trim && !segment.value.trim()) {
                        segmentErrors.value = true;
                        isFormValid = false;
                    }
                }

                return segmentErrors;
            },
        );

        setGroup({ ...group, errors: dynamicSegmentValidation });
        if (!isFormValid) Toast.error(getText('msg_a3ea60e86812'));
        return isFormValid;
    };

    const handleSave = async () => {
        if (!isFormDirty() && initialGroupState.id) {
            Toast.info(getText('msg_67b81126d7df'));
            return;
        }
        if (validateForm()) {
            try {
                const filteredSegments =
                    group.segments.length > 1
                        ? group.segments
                              .slice(0, -1)
                              .concat(
                                  group.segments
                                      .slice(-1)
                                      .filter(
                                          segment =>
                                              segment.item ||
                                              segment.operator ||
                                              segment.value.trim(),
                                      ),
                              )
                        : group.segments;

                const segmentsData = filteredSegments.map(segment => ({
                    itemId: segment.item,
                    operatorId: segment.operator,
                    value: segment.value,
                }));

                const groupInfo = {
                    ...(group.id ? { id: group.id } : {}),
                    name: group.name,
                    segments: segmentsData,
                };

                const response = groupInfo.id
                    ? await updateGroup(groupInfo)
                    : await addGroup(groupInfo);

                if (response) {
                    const successMessage = group.id
                        ? 'Group updated successfully.'
                        : 'Group created successfully.';
                    Toast.success(successMessage);
                    onAddEditGroup(response);
                    if (group.id) {
                        const refreshed = await fetchPopulationGroupByID(
                            group.id,
                        );
                        if (refreshed) {
                            const itemsToLoad = refreshed.segments
                                .map(segment => segment.itemId?.toString())
                                .filter(Boolean);
                            await Promise.all(itemsToLoad.map(loadDataForItem));
                            populateExistingGroup(refreshed);
                        }
                    }
                    return;
                }
            } catch (error) {
                console.error('Save failed:', error);
                Toast.error(
                    error?.response?.data?.detail ||
                        `Unable to ${group.id ? 'update' : 'save'} group: Unknown error`,
                );
            }
        }
    };

    const handleClose = () => {
        if (hasAttribute(SecurityAttributes.PopulationReadOnly)) {
            return onReturnToList();
        }

        if (isFormDirty()) {
            showModal({
                type: ModalTypes.ACTION,
                title: getText('msg_b78130c92f32'),
                subtitle: getText('msg_2f4e8fc9ac09'),
                text: getText('msg_f3cefc556397'),
                onYes: handleSave,
                onNo: onReturnToList,
                noButtonText: getText('msg_ed716b362356'),
                yesButtonText: getText('msg_7af7a6cd22d8'),
            });
        } else {
            onReturnToList();
        }
    };

    const handleAddSegment = () => {
        setGroup(prev => ({
            ...prev,
            segments: [...prev.segments, { item: '', operator: '', value: '' }],
            errors: [
                ...prev.errors,
                { item: null, operator: null, value: null },
            ],
        }));
    };

    const openDefinitionsDrawer = () => setIsDefinitionsDrawerOpen(true);

    const openViewGroupDrawer = () => {
        if (!group.id) {
            Toast.info(getText('msg_59288e911de2'));
            return;
        }
        setIsViewGroupDrawerOpen(true);
    };
    const closeViewGroupDrawer = () => setIsViewGroupDrawerOpen(false);

    const handleSubmit = event => {
        event.preventDefault();
        handleSave();
    };

    const handleNameChange = event => {
        const newName = event.target.value;
        setGroup({ ...group, name: newName });
        if (newName.trim().length > 0) setNameClassName('');
    };

    const isGroupEdited = (obj1, obj2) => {
        if (obj1 === obj2) {
            return true;
        }
        if (
            typeof obj1 !== 'object' ||
            typeof obj2 !== 'object' ||
            obj1 == null ||
            obj2 == null
        ) {
            return obj1 === obj2;
        }

        const keys1 = Object.keys(obj1);
        const keys2 = Object.keys(obj2);

        if (keys1.length !== keys2.length) {
            return false;
        }

        for (const key of keys1) {
            if (!keys2.includes(key)) {
                return false;
            }

            if (key === 'segments') {
                const filteredSegments1 = obj1.segments.filter(
                    segment =>
                        segment.item || segment.operator || segment.value,
                );
                const filteredSegments2 = obj2.segments.filter(
                    segment =>
                        segment.item || segment.operator || segment.value,
                );

                if (!isSameArray(filteredSegments1, filteredSegments2)) {
                    return false;
                }
            } else if (!isGroupEdited(obj1[key], obj2[key])) {
                return false;
            }
        }

        return true;
    };

    function isSameArray(arr1, arr2) {
        if (arr1.length !== arr2.length) {
            return false;
        }

        for (let i = 0; i < arr1.length; i++) {
            if (!isGroupEdited(arr1[i], arr2[i])) {
                return false;
            }
        }

        return true;
    }

    if (isLoading) {
        return <Spinner className={styles.loadingSpinner} width={50} />;
    }

    const isSingleDropdown = (operator, item) => {
        if (operator === '1' || operator === '2') {
            switch (item) {
                case '1':
                case '2':
                case '3':
                case '6':
                case '10':
                case '11':
                case '12':
                case '7':
                case '5':
                case '39':
                case '40':
                case '41':
                case '42':
                case '43':
                case '44':
                case '46':
                case '47':
                case '48':
                case '49':
                case '50':
                case '51':
                case '52':
                case '53':
                case '55':
                    return true;
                default:
                    return false;
            }
        } else {
            return false;
        }
    };

    const isMultipleDropdown = (operator, item) => {
        if (operator === '8' || operator === '9') {
            switch (item) {
                case '1':
                case '2':
                case '3':
                case '6':
                case '10':
                case '12':
                case '11':
                case '7':
                case '5':
                case '39':
                case '40':
                case '41':
                case '42':
                case '43':
                case '44':
                case '46':
                case '47':
                case '48':
                case '49':
                case '50':
                case '51':
                case '52':
                case '53':
                case '55':
                    return true;
                default:
                    return false;
            }
        } else {
            return false;
        }
    };

    const getAttributeData = item => {
        switch (item) {
            case '1':
                return dropdownData.locations;
            case '2':
                return dropdownData.departments;
            case '3':
                return dropdownData.divisions;
            case '6':
                return dropdownData.plans;
            case '7':
                return dropdownData.tiers;
            case '5':
                return dropdownData.planTypes;
            case '10':
                return dropdownData.statuses;
            case '11':
                return dropdownData.fulltimeOrParttime;
            case '12':
                return dropdownData.employeeIDs;
            case '39':
                return dropdownData.regions;
            case '40':
                return dropdownData.businessUnits;
            case '41':
                return dropdownData.payGroups;
            case '42':
                return dropdownData.categories;
            case '43':
                return dropdownData.accountStatuses;
            case '44':
                return dropdownData.languages;
            case '55':
                return dropdownData.workStates;
            case '46':
            case '47':
            case '48':
            case '49':
            case '50':
            case '51':
            case '52':
            case '53':
                return dropdownData.months;
            default:
                return [];
        }
    };

    const extractValue = string => {
        const start = string.indexOf('(') + 1;
        const end = string.indexOf(')');
        return string.substring(start, end);
    };

    // Check if item is Birth Month, Hire Month, Termination Month, or Custom Date 1-5 Months
    const isMonthItem = itemId => {
        const monthItemIds = ['46', '47', '48', '49', '50', '51', '52', '53'];
        return monthItemIds.includes(itemId);
    };

    const getFilteredOperators = itemId => {
        if (!isMonthItem(itemId)) {
            return operators;
        }
        // for month items, only allow: Equal (1), Not Equal (2), In (8), Not In (9)
        const allowedOperatorIds = ['1', '2', '8', '9'];
        return operators.filter(operator =>
            allowedOperatorIds.includes(operator.id.toString()),
        );
    };

    const handleUpdateSearchableDropdown = index => selectedOptions => {
        if (!Array.isArray(selectedOptions)) {
            console.error('Selected options is undefined');
            return;
        }

        //SERIALIZING
        const newValues = selectedOptions.map(option => option.code);
        const updatedSegments = [...group.segments];
        updatedSegments[index].value = newValues.join('~');
        setGroup({ ...group, segments: updatedSegments });
    };

    const handleUpdateSingleSearchableDropdown =
        (index, field) => selectedOptions => {
            if (!Array.isArray(selectedOptions)) {
                console.error('Selected options is undefined');
                return;
            }
            const selectedOption = selectedOptions[0];
            if (selectedOption) {
                if (field === '12') {
                    handleSegmentChange(
                        index,
                        'value',
                        extractValue(selectedOption.name),
                    );
                } else {
                    handleSegmentChange(index, 'value', selectedOption.code);
                }
            }
        };

    const handleDeserializeSavedValues = segment => {
        if (!segment.value) return [];

        const values = segment.value.split('~');
        const data = getAttributeData(segment.item);
        const matchingItems = [];

        for (const item of data) {
            const isMatch = values.includes(item.code);
            if (isMatch) {
                matchingItems.push({ id: item.id });
            }
        }

        return matchingItems;
    };

    const handleDeserializeSingleSavedValue = segment => {
        if (!segment.value) return [];

        const data = getAttributeData(segment.item);
        let matchingItem;

        if (segment.item === '12') {
            matchingItem = data.find(
                item => extractValue(item.name) === segment.value,
            );
        } else {
            matchingItem = data.find(item => item.code === segment.value);
        }

        return matchingItem ? [{ id: matchingItem.id }] : [];
    };

    const getValueDropdownLabel = (segment, isMultiple) => {
        if (isMultiple) {
            const saved = handleDeserializeSavedValues(segment);
            if (saved.length > 1) return `${saved.length} Selected`;
            if (saved.length === 1) {
                const option = getAttributeData(segment.item).find(
                    opt => opt.id === saved[0].id,
                );
                return option?.name ?? 'Select Options';
            }
            return 'Select Options';
        }
        const saved = handleDeserializeSingleSavedValue(segment);
        if (saved.length === 1) {
            const option = getAttributeData(segment.item).find(
                opt => opt.id === saved[0].id,
            );
            return option?.name ?? 'Select Options';
        }
        return 'Select Options';
    };

    const removeValue = (segmentIndex, valueToRemove) => {
        const updatedSegments = [...group.segments];
        const segment = updatedSegments[segmentIndex];
        if (segment && segment.value.includes('~')) {
            //For multiple value segments, split the string, remove the value, and join back
            const valuesArray = segment.value.split('~');
            const updatedValuesArray = valuesArray.filter(
                value => value !== valueToRemove,
            );
            segment.value = updatedValuesArray.join('~');
        } else {
            //For single value segments, just clear the value
            segment.value = '';
        }
        setGroup({ ...group, segments: updatedSegments });

        // If needed, also update the dropdown's state to reflect this change
        // This step might require calling a function similar to handleUpdateSearchableDropdown
        // depending on how you manage state within SearchableDropdown
    };

    return (
        <div className={`card ${styles.AddEditNewGroupForm}`}>
            <div className={`card-body px-3 py-4 ${styles.cardBody}`}>
                <>
                    <div className={`p-3 ${styles.formHeader}`}>
                        <div className={styles.formHeaderTitleRow}>
                            <p
                                className={`page-title ${styles.formPageTitle} mb-0`}
                            >
                                {group.id ? group.name : 'New Group'}
                            </p>
                            <button
                                type="button"
                                className={styles.formCloseButton}
                                onClick={handleClose}
                                aria-label={getText('msg_bbfa773e5a63')}
                            >
                                <i className="bi bi-x" />
                                {getText('msg_bbfa773e5a63')}
                            </button>
                        </div>
                        <p className="text-muted mb-0 mt-1 col-12 col-lg-9">
                            {group.id
                                ? 'Edit population group using rules below. "View Group" will allow you to see which individuals are included in this population based on the rules set.'
                                : 'Send messaging or create content for specific groups of individuals. Create a custom population group using rules below. "View Group" will allow you to see which individuals are included in this population based on the rules set.'}
                        </p>
                    </div>
                    <form
                        onSubmit={handleSubmit}
                        className={`form-container p-3 ${styles.groupForm}`}
                    >
                        <div
                            className={`form-group mb-5 ${styles.nameFieldGroup}`}
                        >
                            <label htmlFor="groupName">
                                {getText('msg_709a23220f2c')}
                                <span className="text-danger">*</span>
                            </label>
                            <input
                                id="groupName"
                                type="text"
                                maxLength="50"
                                className={`form-control ${nameClassName} ${styles.nameClassName}`}
                                value={group.name}
                                onChange={handleNameChange}
                                required
                            />
                            <small className="form-text text-muted">
                                {getText('msg_971d6ded4edf')}{' '}
                                {group?.name?.length}
                                {getText('msg_2fd301945bd1')}
                            </small>
                        </div>

                        <div className="mb-3">
                            <div className={styles.rulesSectionHeader}>
                                <p
                                    className={`page-title ${styles.formPageTitle} mb-0`}
                                >
                                    {getText('msg_bb11a8e3f871')}
                                </p>
                                <button
                                    type="button"
                                    className={styles.viewDefinitionsLink}
                                    onClick={openDefinitionsDrawer}
                                >
                                    {getText('msg_83db64acf280')}
                                </button>
                            </div>
                            <div className={styles.rulesDescriptionRow}>
                                <p className="text-muted mb-0 col-12 col-lg-10">
                                    {getText('msg_d0360130d819')}
                                </p>
                                <div className={styles.viewGroupButtonWrapper}>
                                    <button
                                        type="button"
                                        className="ing-button-primary-fit-content"
                                        onClick={openViewGroupDrawer}
                                    >
                                        {getText('msg_715a7e9b006f')}
                                    </button>
                                </div>
                            </div>
                            <div className={styles.segmentsContainer}>
                                <div
                                    className={`gap-2 mb-2 d-none d-lg-flex justify-content-md-start ${styles.segmentRow}`}
                                >
                                    <label
                                        className={`me-md-2 mb-2 mb-md-0 ${styles.segmentInputItem}`}
                                    >
                                        {getText('msg_ecdda59aea5e')}
                                    </label>
                                    <label
                                        className={`me-md-2 mb-2 mb-md-0 ${styles.segmentInputItem}`}
                                    >
                                        {getText('msg_d0e687b079fb')}
                                    </label>

                                    <label
                                        className={`me-md-2 mb-2 mb-md-0 ${styles.segmentInputItem}`}
                                    >
                                        {getText('msg_8dce170de238')}
                                    </label>

                                    <label
                                        className={`text-danger ${styles.actionColumn}`}
                                    ></label>
                                </div>

                                {group?.segments?.map((segment, index) => {
                                    const showAnd =
                                        index < group.segments.length - 1;
                                    const showDelete =
                                        group.segments.length >
                                            (AUTO_APPEND_SEGMENT ? 2 : 1) &&
                                        !hasAttribute(
                                            SecurityAttributes.PopulationReadOnly,
                                        );
                                    const actionJustify =
                                        showAnd && showDelete
                                            ? 'space-between'
                                            : showDelete
                                              ? 'flex-end'
                                              : 'flex-start';
                                    return (
                                        <React.Fragment key={index}>
                                            <div
                                                className={`col-12 gap-2 mb-2 d-lg-flex justify-content-md-start ${styles.segmentRow}`}
                                            >
                                                <div
                                                    className={`me-md-2 mb-2  ${styles.segmentInputItem}`}
                                                >
                                                    <label
                                                        className={`me-md-2 mb-2 mb-md-0 d-block d-lg-none`}
                                                    >
                                                        {getText(
                                                            'msg_ecdda59aea5e',
                                                        )}
                                                    </label>

                                                    <Dropdown
                                                        allowSearch
                                                        name={`item-${index}`}
                                                        defaultValue={() => {
                                                            const matchingItem =
                                                                items.find(
                                                                    item =>
                                                                        item.id.toString() ===
                                                                        segment.item,
                                                                );
                                                            return matchingItem
                                                                ? matchingItem.name
                                                                : 'Select Item';
                                                        }}
                                                        options={items.map(
                                                            item => ({
                                                                label: item.name,
                                                                value: item.id.toString(),
                                                            }),
                                                        )}
                                                        onChange={value => {
                                                            handleSegmentChange(
                                                                index,
                                                                'item',
                                                                value,
                                                            );
                                                        }}
                                                        value={
                                                            segment.item || ''
                                                        }
                                                        disabled={
                                                            items.length === 0
                                                        }
                                                        className={
                                                            group?.errors[index]
                                                                ?.item
                                                                ? 'is-invalid'
                                                                : ''
                                                        }
                                                    />
                                                </div>
                                                <div
                                                    className={`me-md-2 mb-2  ${styles.segmentInputItem}`}
                                                >
                                                    <label
                                                        className={`me-md-2 mb-2 mb-md-0 d-block d-lg-none`}
                                                    >
                                                        {getText(
                                                            'msg_d0e687b079fb',
                                                        )}
                                                    </label>
                                                    <Dropdown
                                                        allowSearch
                                                        name={`operator-${index}`}
                                                        defaultValue={() => {
                                                            const matchingOperator =
                                                                getFilteredOperators(
                                                                    segment.item,
                                                                ).find(
                                                                    op =>
                                                                        op.id.toString() ===
                                                                        segment.operator,
                                                                );
                                                            return matchingOperator
                                                                ? matchingOperator.name
                                                                : 'Select Operator';
                                                        }}
                                                        options={getFilteredOperators(
                                                            segment.item,
                                                        ).map(operator => ({
                                                            label: operator.name,
                                                            value: operator.id.toString(),
                                                        }))}
                                                        onChange={value => {
                                                            handleSegmentChange(
                                                                index,
                                                                'operator',
                                                                value,
                                                            );
                                                        }}
                                                        value={
                                                            segment.operator ||
                                                            ''
                                                        }
                                                        disabled={
                                                            operators.length ===
                                                            0
                                                        }
                                                        className={
                                                            group?.errors[index]
                                                                ?.operator
                                                                ? 'is-invalid'
                                                                : ''
                                                        }
                                                    />
                                                </div>
                                                <div
                                                    className={`me-md-2 mb-2  ${styles.segmentInputItem}`}
                                                >
                                                    <label
                                                        className={`me-md-2 mb-2 mb-md-0 d-block d-lg-none`}
                                                    >
                                                        {getText(
                                                            'msg_8dce170de238',
                                                        )}
                                                    </label>
                                                    {isMultipleDropdown(
                                                        segment.operator,
                                                        segment.item,
                                                    ) ? (
                                                        <SearchableDropdown
                                                            options={getAttributeData(
                                                                segment.item,
                                                            )}
                                                            optionIDField="id"
                                                            savedOptions={handleDeserializeSavedValues(
                                                                segment,
                                                            )}
                                                            savedOptionIDField="id"
                                                            updateOptions={handleUpdateSearchableDropdown(
                                                                index,
                                                            )}
                                                            showTags={false}
                                                            placeholderLabel={getValueDropdownLabel(
                                                                segment,
                                                                true,
                                                            )}
                                                        />
                                                    ) : isSingleDropdown(
                                                          segment.operator,
                                                          segment.item,
                                                      ) ? (
                                                        <SearchableDropdown
                                                            options={getAttributeData(
                                                                segment.item,
                                                            )}
                                                            optionIDField="id"
                                                            savedOptions={handleDeserializeSingleSavedValue(
                                                                segment,
                                                            )}
                                                            savedOptionIDField="id"
                                                            updateOptions={handleUpdateSingleSearchableDropdown(
                                                                index,
                                                                segment.item,
                                                            )}
                                                            showTags={false}
                                                            maxSelections={1}
                                                            placeholderLabel={getValueDropdownLabel(
                                                                segment,
                                                                false,
                                                            )}
                                                        />
                                                    ) : (
                                                        <input
                                                            className={`form-control ${group?.errors[index]?.value ? 'is-invalid' : ''}`}
                                                            placeholder={getText(
                                                                'msg_a4c9cbd6e25f',
                                                            )}
                                                            value={
                                                                segment.value
                                                            }
                                                            onChange={e =>
                                                                handleSegmentChange(
                                                                    index,
                                                                    'value',
                                                                    e.target
                                                                        .value,
                                                                )
                                                            }
                                                        />
                                                    )}
                                                </div>
                                                <div
                                                    className={
                                                        styles.actionColumn
                                                    }
                                                    style={{
                                                        justifyContent:
                                                            actionJustify,
                                                    }}
                                                >
                                                    {showAnd && (
                                                        <span
                                                            className={
                                                                styles.andConnector
                                                            }
                                                        >
                                                            {getText(
                                                                'msg_c2f934cb8cab',
                                                            )}
                                                        </span>
                                                    )}
                                                    {showDelete && (
                                                        <button
                                                            type="button"
                                                            className={
                                                                styles.deleteSegmentButton
                                                            }
                                                            onClick={() =>
                                                                handleRemoveSegment(
                                                                    index,
                                                                )
                                                            }
                                                        >
                                                            <i className="bi bi-trash3" />
                                                            {getText(
                                                                'msg_f6fdbe48dc54',
                                                            )}
                                                        </button>
                                                    )}
                                                </div>
                                            </div>
                                        </React.Fragment>
                                    );
                                })}
                                {!AUTO_APPEND_SEGMENT &&
                                    !hasAttribute(
                                        SecurityAttributes.PopulationReadOnly,
                                    ) && (
                                        <button
                                            type="button"
                                            className={`p-0 mt-4 d-flex align-items-center gap-2 ${styles.addRuleButton}`}
                                            onClick={handleAddSegment}
                                        >
                                            <span
                                                className={styles.addRuleIcon}
                                            >
                                                <i className="bi bi-plus" />
                                            </span>
                                            {getText('msg_d0b595248d0e')}
                                        </button>
                                    )}
                            </div>
                        </div>
                    </form>
                    <div className={`p-3 ${styles.formActions}`}>
                        <button
                            type="button"
                            className="ing-button-primary-fit-content"
                            onClick={handleSave}
                        >
                            {getText('msg_efc007a393f6')}
                        </button>
                    </div>
                    <SideDrawer
                        isOpen={isDefinitionsDrawerOpen}
                        setIsOpen={setIsDefinitionsDrawerOpen}
                        header={getText('msg_c60bb0cab7d8')}
                        sections={OPERATOR_DEFINITION_SECTIONS}
                    />
                    <ViewGroupDrawer
                        isOpen={isViewGroupDrawerOpen}
                        closeDrawer={closeViewGroupDrawer}
                        groupId={group.id}
                        groupName={group.name}
                        isDirty={isFormDirty()}
                    />
                </>
            </div>
        </div>
    );
};

AddEditNewGroupForm.propTypes = {
    onAddEditGroup: PropTypes.func.isRequired,
    onReturnToList: PropTypes.func.isRequired,
    groupId: PropTypes.string,
};

export default AddEditNewGroupForm;
